import { VerifyBar } from "./VerifyBar";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Tv, Smartphone, Mic, Loader2, Radio, Volume2, Sparkles, Plus, Trash2, X, Zap, Link2, MessageSquare, Users, Phone, MapPin, Wand2, TrendingUp, Target, Gavel, Briefcase, Stethoscope, ShoppingBag, Bitcoin, Globe, Brain, Clapperboard, ShieldCheck, Swords, GitBranch } from "lucide-react";
import { nexusTalk, nexusChannel, nexusCommand, audioUrlForKey, nexusGetProfile, nexusSaveProfile, nexusListMemory, nexusAddMemory, nexusDelMemory } from "../lib/api";
import NexusTV from "./NexusTV";
import NexusChat from "./NexusChat";
import NexusFamily from "./NexusFamily";
import NexusPhone from "./NexusPhone";
import NexusTrack from "./NexusTrack";
import NexusStudio from "./NexusStudio";
import NexusVideoIntel from "./NexusVideoIntel";
import NexusVerdict from "./NexusVerdict";
import NexusPortfolio from "./NexusPortfolio";
import NexusDrPitt from "./NexusDrPitt";
import NexusShop from "./NexusShop";
import NexusScrape from "./NexusScrape";
import NexusConnections from "./NexusConnections";
import NexusBriefing from "./NexusBriefing";
import NexusAnnouncer from "./NexusAnnouncer";
import NexusPonytail from "./NexusPonytail";
import DevBridge from "./DevBridge";
import NexusMissAnne from "./NexusMissAnne";
import HerculesFloor from "./HerculesFloor";
import NexusClipsy from "./NexusClipsy";
import { toast } from "sonner";
import { useOwnerLocation } from "../hooks/useOwnerLocation";

const MOOD_COLOR = { danger: "#EF4444", good: "#10B981", neutral: "#00F0FF" };

// Type a command → Nexus answers here AND on the TV (shared channel), speaks aloud.
function QuickCommand({ session }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState("");
  const [mood, setMood] = useState("neutral");
  const [listening, setListening] = useState(false);
  const audioRef = useRef(null);
  const recogRef = useRef(null);

  const send = async (q) => {
    const msg = (q ?? text).trim();
    if (!msg || busy) return;
    setBusy(true); setReply("");
    try {
      const r = await nexusCommand(session.user_id, msg);
      setReply(r.reply_text || ""); setMood(r.mood || "neutral"); setText("");
      if (r.audio_key && audioRef.current) {
        audioRef.current.src = audioUrlForKey(r.audio_key);
        audioRef.current.play().catch(() => {});
      }
    } catch (e) { toast.error("Nexus could not answer"); }
    finally { setBusy(false); }
  };

  const dictate = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.message("Voice typing needs Chrome/Safari — just type it"); return; }
    if (listening) { try { recogRef.current?.stop(); } catch (e) {} return; }
    const recog = new SR(); recog.lang = "en-US"; recog.interimResults = false;
    recog.onresult = (ev) => { const t = ev.results[0][0].transcript; setText(t); send(t); };
    recog.onend = () => setListening(false);
    recog.onerror = () => setListening(false);
    recogRef.current = recog; setListening(true); try { recog.start(); } catch (e) {}
  };

  const c = MOOD_COLOR[mood] || "#00F0FF";
  return (
    <div className="nx-glass rounded-2xl p-4 space-y-3" data-testid="nexus-quick-command">
      <p className="nx-mono text-[11px] tracking-[0.2em] text-cyan-300/60">TELL NEXUS — SHOWS ON YOUR TV TOO</p>
      <div className="flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }}
          placeholder="Type a command…  e.g. how's my business today?" data-testid="nexus-command-input"
          className="flex-1 bg-[#030712] text-cyan-50 border border-[#00F0FF44] rounded-xl px-3 py-3 text-sm focus:outline-none focus:border-[#00F0FF] min-w-0" />
        <button onClick={dictate} data-testid="nexus-command-mic"
          className={`px-3 rounded-xl border ${listening ? "text-[#10B981] border-[#10B981] animate-pulse" : "text-[#00F0FF] border-[#00F0FF44]"}`}>
          <Mic className="w-5 h-5" />
        </button>
        <button onClick={() => send()} disabled={busy || !text.trim()} data-testid="nexus-command-send"
          className="px-4 rounded-xl nx-orbitron font-bold text-[#030712] bg-[#00F0FF] disabled:opacity-40 flex items-center">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send"}
        </button>
      </div>
      {reply && <p className="text-cyan-50 text-sm leading-relaxed pl-2 border-l-2" style={{ borderColor: c }} data-testid="nexus-command-reply">{reply}</p>}
      <audio ref={audioRef} />
    </div>
  );
}

const WAKE_WORD = "nexus";

// ---- Phone / mic mode ----
function PhoneMode({ session, solo = false }) {
  const [sessionId, setSessionId] = useState(null);
  const [status, setStatus] = useState("idle");
  const [lastUser, setLastUser] = useState("");
  const [lastReply, setLastReply] = useState("");
  const [wake, setWake] = useState(false);
  const [playHere, setPlayHere] = useState(solo);
  const [recording, setRecording] = useState(false);

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const recogRef = useRef(null);
  const busyRef = useRef(false);
  const localAudio = useRef(null);
  const armedRef = useRef(false);
  const armTimerRef = useRef(null);
  const wakeSentRef = useRef("");

  useEffect(() => {
    (async () => {
      try { const r = await nexusChannel(session.user_id); setSessionId(r.session_id); }
      catch (e) { toast.error("Could not start Nexus"); }
    })();
  }, [session.user_id]);

  // Send a spoken command as TEXT (via SpeechRecognition transcript) — works on iPhone foreground
  const sendText = useCallback(async (cmd) => {
    if (busyRef.current || !cmd) return;
    busyRef.current = true;
    armedRef.current = false;
    setStatus("thinking");
    setLastUser(cmd);
    try {
      const r = await nexusCommand(session.user_id, cmd);
      setLastReply(r.reply_text || "");
      setStatus("speaking");
      if (playHere && r.audio_key && localAudio.current) {
        const a = localAudio.current;
        a.src = audioUrlForKey(r.audio_key);
        // stay "busy" (ignore mic) until Nexus finishes speaking so it never hears itself
        a.onended = () => { busyRef.current = false; setStatus("idle"); };
        a.play().catch(() => { busyRef.current = false; setStatus("idle"); });
        setTimeout(() => { if (busyRef.current) { busyRef.current = false; setStatus("idle"); } }, 20000);
        return;
      }
      setTimeout(() => { busyRef.current = false; setStatus("idle"); }, 2500);
    } catch (e) {
      toast.error("Nexus could not answer");
      busyRef.current = false;
      setStatus("idle");
    }
  }, [session.user_id, playHere]);

  const sendBlob = useCallback(async (blob) => {
    if (!sessionId || !blob || blob.size < 400) { setStatus("idle"); if (blob && blob.size < 400) toast.message("Didn't catch that — tap and speak a little longer."); return; }
    setStatus("thinking");
    busyRef.current = true;
    try {
      const r = await nexusTalk(sessionId, session.user_id, blob);
      setLastUser(r.user_text || "");
      setLastReply(r.reply_text || "");
      if (playHere && r.audio_key && localAudio.current) {
        localAudio.current.src = audioUrlForKey(r.audio_key);
        localAudio.current.play().catch(() => {});
      }
      setStatus("speaking");
      setTimeout(() => setStatus("idle"), 2500);
    } catch (e) {
      toast.error("Nexus could not answer");
      setStatus("idle");
    } finally { busyRef.current = false; }
  }, [sessionId, session.user_id, playHere]);

  const startRecording = useCallback(async (autoStop = true) => {
    if (recording || busyRef.current) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      // pick a mimeType the browser actually supports (Safari differs from Chrome)
      let mime = "";
      const cands = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
      for (const c of cands) { if (window.MediaRecorder && MediaRecorder.isTypeSupported(c)) { mime = c; break; } }
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        sendBlob(blob);
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
      setStatus("listening");

      if (autoStop) {
        // Silence-based auto stop using Web Audio RMS
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const src = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        src.connect(analyser);
        const data = new Uint8Array(analyser.frequencyBinCount);
        const startedAt = Date.now();
        let quietSince = null;
        const tick = () => {
          if (mediaRef.current?.state !== "recording") { ctx.close().catch(() => {}); return; }
          analyser.getByteTimeDomainData(data);
          let sum = 0;
          for (let i = 0; i < data.length; i++) { const v = (data[i] - 128) / 128; sum += v * v; }
          const rms = Math.sqrt(sum / data.length);
          const elapsed = Date.now() - startedAt;
          if (rms < 0.02) { if (!quietSince) quietSince = Date.now(); }
          else { quietSince = null; }
          const quietFor = quietSince ? Date.now() - quietSince : 0;
          if ((elapsed > 900 && quietFor > 1100) || elapsed > 12000) {
            try { mediaRef.current.stop(); } catch (e) {}
            ctx.close().catch(() => {});
            return;
          }
          requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    } catch (e) {
      toast.error("Microphone blocked — allow mic access");
      setRecording(false);
    }
  }, [recording, sendBlob]);

  const stopRecording = useCallback(() => {
    if (mediaRef.current?.state === "recording") { try { mediaRef.current.stop(); } catch (e) {} }
  }, []);

  // "Hey Nexus" wake word — uses live speech-to-text (no second mic grab), so it works
  // on desktop Chrome, Android, AND iPhone Safari (16.4+) while the app is in the foreground.
  useEffect(() => {
    if (!wake || !sessionId) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.message("This browser can't do voice. Use Chrome, Android, or update to iOS 16.4+ Safari — or tap the mic / type.", { duration: 7000 });
      setWake(false);
      return;
    }
    let stopped = false;
    let restartTimer = null;
    const recog = new SR();
    recog.continuous = true;
    recog.interimResults = true;
    recog.lang = "en-US";

    const process = (transcript) => {
      const clean = transcript.trim();
      if (!clean || clean === wakeSentRef.current) return;
      const low = clean.toLowerCase();
      const idx = low.lastIndexOf("nexus");
      if (idx !== -1) {
        // strip through the wake word; whatever follows is the command
        let cmd = clean.slice(idx + 5).replace(/^[\s,.:!?—-]+/, "").trim();
        if (cmd.split(/\s+/).filter(Boolean).length >= 2) {
          wakeSentRef.current = clean;
          sendText(cmd);
        } else {
          // heard "Hey Nexus" with no command yet — arm for the next thing they say
          armedRef.current = true;
          setStatus("listening");
          if (armTimerRef.current) clearTimeout(armTimerRef.current);
          armTimerRef.current = setTimeout(() => { armedRef.current = false; setStatus("idle"); }, 9000);
        }
      } else if (armedRef.current && clean.split(/\s+/).filter(Boolean).length >= 2) {
        wakeSentRef.current = clean;
        sendText(clean);
      }
    };

    recog.onresult = (ev) => {
      if (busyRef.current) return;
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        if (ev.results[i].isFinal) process(ev.results[i][0].transcript);
      }
    };
    recog.onend = () => {
      if (!stopped) restartTimer = setTimeout(() => { try { recog.start(); } catch (e) {} }, 350);
    };
    recog.onerror = (e) => {
      const err = e && e.error;
      if (err === "not-allowed" || err === "service-not-allowed") {
        toast.error("Microphone blocked — allow mic access in your browser to use Hey Nexus.");
        setWake(false);
      } else if (err === "network") {
        toast.error("Your network is blocking the speech service (common on locked-down office PCs). Use the mic button — it goes through Nexus's own servers.", { duration: 9000 });
        setWake(false);
      } else if (err === "audio-capture") {
        toast.error("No microphone found. Check your mic, then try again.");
        setWake(false);
      }
      // 'no-speech' / 'aborted' are normal; onend will restart the listener
    };
    recogRef.current = recog;
    try { recog.start(); } catch (e) {}
    toast.success("Listening — say “Hey Nexus …”");
    return () => {
      stopped = true;
      if (restartTimer) clearTimeout(restartTimer);
      if (armTimerRef.current) clearTimeout(armTimerRef.current);
      armedRef.current = false;
      try { recog.stop(); } catch (e) {}
    };
  }, [wake, sessionId, sendText]);

  if (!sessionId) {
    return (
      <div className="min-h-[calc(100vh-56px)] bg-[#030712] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  const micColor = recording ? "#00F0FF" : status === "thinking" ? "#FFB703" : "#0aa5c9";
  return (
    <div className="h-full bg-[#030712] nx-display nx-scanlines flex flex-col items-center justify-center p-6 gap-8">
      <audio ref={localAudio} />
      <p className="nx-orbitron text-[#00F0FF] tracking-[0.3em] font-black text-lg">NEXUS · MIC</p>
      <div className="flex items-center justify-center gap-3">
        <button onClick={() => {
            try { if (localAudio.current) { localAudio.current.muted = true; localAudio.current.play().catch(() => {}); localAudio.current.pause(); localAudio.current.muted = false; } } catch (e) {}
            setWake((w) => !w);
          }} data-testid="nexus-wake-toggle"
          className={`px-4 py-2 rounded-full nx-mono text-xs font-bold flex items-center gap-2 border transition-colors ${wake ? "bg-[#10B981] text-black border-[#10B981]" : "text-cyan-200/70 border-cyan-200/30"}`}>
          <Radio className={`w-4 h-4 ${wake ? "animate-pulse" : ""}`} /> HEY NEXUS {wake ? "ON" : "OFF"}
        </button>
        <button onClick={() => setPlayHere((p) => !p)} data-testid="nexus-playhere-toggle"
          className={`px-4 py-2 rounded-full nx-mono text-xs font-bold flex items-center gap-2 border transition-colors ${playHere ? "bg-[#3B82F6] text-white border-[#3B82F6]" : "text-cyan-200/70 border-cyan-200/30"}`}>
          <Volume2 className="w-4 h-4" /> HEAR HERE
        </button>
      </div>

      <div className="flex justify-center relative">
        {recording && <span className="absolute inset-0 rounded-full nx-ripple" style={{ border: "2px solid #00F0FF" }} />}
        <button
          data-testid="nexus-mic-btn"
          onClick={() => { if (recording) stopRecording(); else startRecording(false); }}
          className={`rounded-full flex items-center justify-center transition-transform ${recording ? "scale-110" : ""}`}
          style={{ width: 190, height: 190, background: `radial-gradient(circle at 50% 35%, ${micColor}, #061018 72%)`, boxShadow: `0 0 70px ${micColor}${recording ? "cc" : "66"}, inset 0 0 40px #000a` }}>
          {status === "thinking" ? <Loader2 className="w-16 h-16 text-white animate-spin" /> : <Mic className="w-16 h-16 text-white" />}
        </button>
      </div>
      <p className="text-center text-cyan-200/50 text-sm nx-mono" data-testid="nexus-phone-status">
        {status === "thinking" ? "NEXUS IS THINKING…"
          : status === "speaking" ? "NEXUS IS SPEAKING…"
          : status === "listening" ? (recording ? "RECORDING — TAP TO SEND" : "YES, SIR — GO AHEAD…")
          : wake ? "LISTENING FOR “HEY NEXUS”" : "TAP THE MIC TO TALK · OR TURN ON “HEY NEXUS”"}
      </p>

      <div className="space-y-2 text-center min-h-[60px] max-w-md">
        {lastUser && <p className="text-cyan-300/60 nx-mono text-sm" data-testid="nexus-phone-user">▸ {lastUser}</p>}
        {lastReply && <p className="font-semibold text-lg text-cyan-50" data-testid="nexus-phone-reply">{lastReply}</p>}
      </div>
    </div>
  );
}

export const NEXUS_MORE = [
  { role: "solo", label: "TALK", icon: Mic }, { role: "phone", label: "PHONE", icon: Phone }, { role: "studio", label: "STUDIO", icon: Wand2 },
  { role: "video", label: "INTEL", icon: TrendingUp }, { role: "portfolio", label: "PORTFOLIO", icon: Briefcase }, { role: "verify", label: "VERIFY", icon: ShieldCheck }, { role: "shop", label: "SHOP", icon: ShoppingBag },
  { role: "scrape", label: "SCRAPE", icon: Globe }, { role: "drpitt", label: "DR PITT", icon: Stethoscope }, { role: "verdict", label: "VERDICT", icon: Gavel },
  { role: "family", label: "FAMILY", icon: Users }, { role: "track", label: "MAP", icon: MapPin }, { role: "tv", label: "SCREEN", icon: Tv },
];

export default function NexusAssistant({ session, role: roleProp, onRoleChange, hideModes = false }) {
  const [roleS, setRoleS] = useState("chat");
  const role = roleProp ?? roleS;
  const setRole = onRoleChange ?? setRoleS;
  const [showPersonalize, setShowPersonalize] = useState(false);
  useOwnerLocation();
  const [showConnections, setShowConnections] = useState(false);

  const modes = [
    { id: "chat", label: "CHAT", icon: MessageSquare },
    { id: "ponytail", label: "PONYTAIL", icon: Brain },
    { id: "devbridge", label: "DEV BRIDGE", icon: GitBranch },
    { id: "hercules", label: "HERCULES FLOOR", icon: Swords },
    { id: "clipsy", label: "CLIPSY", icon: Clapperboard },
    { id: "solo", label: "TALK", icon: Mic },
    { id: "phone", label: "PHONE", icon: Phone },
    { id: "studio", label: "STUDIO", icon: Wand2 },
    { id: "video", label: "INTEL", icon: TrendingUp },
    { id: "portfolio", label: "PORTFOLIO", icon: Briefcase },
    { id: "shop", label: "SHOP", icon: ShoppingBag },
    { id: "scrape", label: "SCRAPE", icon: Globe },
    { id: "drpitt", label: "DR PITT", icon: Stethoscope },
    { id: "verdict", label: "VERDICT", icon: Gavel },
    { id: "family", label: "FAMILY", icon: Users },
    { id: "track", label: "MAP", icon: MapPin },
    { id: "tv", label: "SCREEN", icon: Tv },
  ];

  if (role === "tv") {
    return (
      <div className="relative">
        <NexusAnnouncer userId={session.user_id} />
        <NexusTV session={session} />
        <button onClick={() => setRole("chat")} data-testid="nexus-exit-tv"
          className="fixed top-2 left-4 z-50 px-3 py-1.5 rounded-full nx-mono text-[10px] tracking-widest text-[#00F0FF] bg-[#030712cc] border border-[#00F0FF44] hover:bg-[#00F0FF11]">← CHAT</button>
      </div>
    );
  }

  return (
    <div className={`${hideModes ? "h-full" : "h-[calc(100dvh-56px)]"} flex flex-col bg-[#030712] nx-display`}>
      <NexusAnnouncer userId={session.user_id} />
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#00F0FF22] shrink-0">
        <div className={`items-center gap-1 overflow-x-auto ${hideModes ? "hidden" : "flex"}`}>
          {modes.map((m) => (
            <button key={m.id} onClick={() => setRole(m.id)} data-testid={`nexus-mode-${m.id}`}
              className={`px-3 py-1.5 rounded-full nx-mono text-[10px] tracking-widest font-bold flex items-center gap-1.5 transition-colors ${role === m.id ? "text-[#030712] bg-[#00F0FF]" : "text-cyan-200/60 hover:text-cyan-100"}`}>
              <m.icon className="w-3.5 h-3.5" /> {m.label}
            </button>
          ))}
        </div>
        {hideModes && <p className="nx-orbitron text-[11px] tracking-[0.3em] text-[#00F0FF] truncate" data-testid="nexus-current-mode">{(modes.find((m) => m.id === role) || modes[0]).label}</p>}
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => setShowPersonalize(true)} data-testid="nexus-personalize-open"
            className="px-3 py-1.5 rounded-full nx-mono text-[10px] tracking-widest text-[#00F0FF] border border-[#00F0FF33] hover:bg-[#00F0FF11] flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> SETUP</button>
          <button onClick={() => setShowConnections(true)} data-testid="nexus-connections-open"
            className="px-3 py-1.5 rounded-full nx-mono text-[10px] tracking-widest text-[#00F0FF] border border-[#00F0FF33] hover:bg-[#00F0FF11] flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> CONNECT</button>
        </div>
      </div>
      <div className="flex-1 min-h-0">
        {role === "chat" ? <NexusChat session={session} />
          : role === "ponytail" ? <NexusPonytail session={session} />
          : role === "devbridge" ? <DevBridge />
          : role === "anne" ? <NexusMissAnne />
          : role === "hercules" ? <HerculesFloor />
          : role === "clipsy" ? <NexusClipsy />
          : role === "family" ? <NexusFamily session={session} />
          : role === "phone" ? <NexusPhone session={session} />
          : role === "studio" ? <NexusStudio session={session} />
          : role === "video" ? <NexusVideoIntel session={session} />
          : role === "verdict" ? <NexusVerdict session={session} />
          : role === "verify" ? <div className="p-3 max-w-2xl mx-auto w-full overflow-y-auto"><VerifyBar compact /></div>
          : role === "portfolio" ? <NexusPortfolio session={session} />
          : role === "shop" ? <NexusShop session={session} />
          : role === "scrape" ? <NexusScrape session={session} />
          : role === "drpitt" ? <NexusDrPitt session={session} />
          : role === "track" ? <NexusTrack session={session} />
          : <PhoneMode session={session} solo={role === "solo"} />}
      </div>
      {showPersonalize && <Personalize session={session} onClose={() => setShowPersonalize(false)} />}
      {showConnections && <NexusConnections session={session} onClose={() => setShowConnections(false)} />}
    </div>
  );
}

function Personalize({ session, onClose }) {
  const [prof, setProf] = useState({ name: "Sohel Patel", phone: "", about: "", family: [] });
  const [mem, setMem] = useState([]);
  const [newMem, setNewMem] = useState("");
  const [fam, setFam] = useState({ name: "", relation: "", phone: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [p, m] = await Promise.all([nexusGetProfile(session.user_id), nexusListMemory(session.user_id)]);
        if (p) setProf({ name: p.name || "Sohel Patel", phone: p.phone || "", about: p.about || "", family: p.family || [] });
        setMem(m || []);
      } catch (e) {}
    })();
  }, [session.user_id]);

  const save = async () => {
    setSaving(true);
    try { await nexusSaveProfile({ user_id: session.user_id, ...prof }); toast.success("Nexus updated"); onClose(); }
    catch (e) { toast.error("Could not save"); } finally { setSaving(false); }
  };
  const addFam = () => { if (fam.name) { setProf((p) => ({ ...p, family: [...p.family, fam] })); setFam({ name: "", relation: "", phone: "" }); } };
  const rmFam = (i) => setProf((p) => ({ ...p, family: p.family.filter((_, x) => x !== i) }));
  const addMemory = async () => { if (!newMem.trim()) return; await nexusAddMemory(session.user_id, newMem.trim()); setMem((m) => [{ id: Math.random().toString(), text: newMem.trim(), auto: false }, ...m]); setNewMem(""); };
  const delMemory = async (id) => { await nexusDelMemory(id, session.user_id); setMem((m) => m.filter((x) => x.id !== id)); };

  const inp = "w-full bg-[#030712] text-cyan-50 border border-[#00F0FF44] rounded-xl px-3 py-2 nx-mono text-sm focus:outline-none focus:border-[#00F0FF]";
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 nx-display" data-testid="nexus-personalize-modal">
      <div className="nx-glass rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-5 relative">
        <button onClick={onClose} data-testid="nexus-personalize-close" className="absolute top-4 right-4 text-cyan-200/60 hover:text-white"><X className="w-6 h-6" /></button>
        <h2 className="nx-orbitron text-xl font-black tracking-widest text-[#00F0FF] flex items-center gap-2"><Sparkles className="w-5 h-5" /> ACQUAINT NEXUS</h2>

        <div className="space-y-3">
          <div><label className="text-cyan-200/60 text-xs nx-mono">YOUR NAME</label><input className={inp} value={prof.name} onChange={(e) => setProf({ ...prof, name: e.target.value })} data-testid="nexus-prof-name" /></div>
          <div><label className="text-cyan-200/60 text-xs nx-mono">YOUR PHONE</label><input className={inp} value={prof.phone} onChange={(e) => setProf({ ...prof, phone: e.target.value })} data-testid="nexus-prof-phone" placeholder="+1..." /></div>
          <div><label className="text-cyan-200/60 text-xs nx-mono">ABOUT YOU (work, goals, anything)</label><textarea className={inp} rows={3} value={prof.about} onChange={(e) => setProf({ ...prof, about: e.target.value })} data-testid="nexus-prof-about" /></div>
        </div>

        <div className="space-y-2">
          <p className="text-cyan-200/60 text-xs nx-mono tracking-widest">FAMILY & CLOSE CONTACTS</p>
          {prof.family.map((f, i) => (
            <div key={i} className="flex items-center justify-between bg-[#0a1224] rounded-lg px-3 py-2 text-sm text-cyan-50">
              <span>{f.name} · {f.relation}{f.phone ? ` · ${f.phone}` : ""}</span>
              <button onClick={() => rmFam(i)} className="text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <div className="grid grid-cols-3 gap-2">
            <input className={inp} placeholder="Name" value={fam.name} onChange={(e) => setFam({ ...fam, name: e.target.value })} data-testid="nexus-fam-name" />
            <input className={inp} placeholder="Relation" value={fam.relation} onChange={(e) => setFam({ ...fam, relation: e.target.value })} data-testid="nexus-fam-rel" />
            <input className={inp} placeholder="Phone" value={fam.phone} onChange={(e) => setFam({ ...fam, phone: e.target.value })} data-testid="nexus-fam-phone" />
          </div>
          <button onClick={addFam} className="text-[#00F0FF] text-sm flex items-center gap-1 nx-mono" data-testid="nexus-fam-add"><Plus className="w-4 h-4" /> Add person</button>
        </div>

        <div className="space-y-2">
          <p className="text-cyan-200/60 text-xs nx-mono tracking-widest flex items-center gap-1"><Zap className="w-3 h-3" /> WHAT NEXUS KNOWS ({mem.length}) — he also learns this automatically</p>
          <div className="flex gap-2">
            <input className={inp} placeholder="Add a fact (e.g. I dislike cilantro)" value={newMem} onChange={(e) => setNewMem(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addMemory(); }} data-testid="nexus-mem-input" />
            <button onClick={addMemory} className="px-3 rounded-xl bg-[#00F0FF] text-[#030712]" data-testid="nexus-mem-add"><Plus className="w-4 h-4" /></button>
          </div>
          <div className="max-h-40 overflow-y-auto space-y-1">
            {mem.map((m) => (
              <div key={m.id} className="flex items-center justify-between bg-[#0a1224] rounded-lg px-3 py-1.5 text-sm text-cyan-50">
                <span>{m.text} {m.auto && <span className="text-[10px] text-cyan-400/50 nx-mono">· learned</span>}</span>
                <button onClick={() => delMemory(m.id)} className="text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        </div>

        <button onClick={save} disabled={saving} data-testid="nexus-personalize-save"
          className="w-full py-3 rounded-2xl nx-orbitron font-bold tracking-widest uppercase bg-[#00F0FF] text-[#030712] disabled:opacity-40">
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
